<template>
  <div class="wrap-layout">
    <a-layout style="height: 100%">
      <!-- 侧边栏 -->
      <a-layout-sider
        theme="light"
        v-model:collapsed="collapsed"
        :trigger="null"
        collapsible
        width="230"
      >
        <layout-sider :collapsed="collapsed" />
      </a-layout-sider>
      <a-layout>

        <!-- 头部 -->
        <a-layout-header class="layout-header">
          <layout-header v-model:collapsed="collapsed" />
        </a-layout-header>

        <!-- 内容主体 -->
        <a-layout-content>
          <!-- 标签页 -->
          <div class="tabs">
            <a-tabs
              v-model:activeKey="activeKey"
              hideAdd
              type="editable-card"
              :tabBarStyle="{ background: '#fff' }"
              @edit="onEdit"
              @change="onTabChange"
            >
              <a-tab-pane v-for="(pane, index) in settingStore.menuTabs" :key="pane.key">
                <template #tab>
                  <a-dropdown :trigger="['contextmenu']">
                    <div @click="router.push(pane.key)">
                      <span>
                        <component :is="pane.icon" />
                        {{ pane.title }}
                      </span>
                    </div>
                    
                    <template #overlay>
                      <a-menu @click="closeTab($event, index)">
                        <a-menu-item key="1">
                          <VerticalAlignMiddleOutlined />
                          关闭当前
                        </a-menu-item>
                        <a-menu-item key="2">
                          <VerticalLeftOutlined />
                          关闭右侧
                        </a-menu-item>
                        <a-menu-item key="3">
                          <VerticalRightOutlined />
                          关闭左侧
                        </a-menu-item>
                        <a-menu-item key="4">
                          <ScissorOutlined />
                          关闭全部
                        </a-menu-item>
                      </a-menu>
                    </template>
                  </a-dropdown>
                </template>
              </a-tab-pane>
            </a-tabs>
          </div>

          <div class="content" :style="{ minHeight: '280px' }">
            <div class="content-scroll">
              <router-view v-slot="{ Component, route: currentRoute }">
                <keep-alive :include="cachedPaths">
                  <component :is="wrap(currentRoute.path, Component)" :key="currentRoute.path" />
                </keep-alive>
              </router-view>
            </div>
            <div style="margin-top: 10px;text-align: center;">Copyright © 2014-2024 {{ systemName }} v5.5.0</div> 
          </div>
          
        </a-layout-content>
      </a-layout>
    </a-layout>
  </div>
</template>

<script lang="ts" setup>
import LayoutSider from './components/layout-sider.vue';
import LayoutHeader from './components/layout-header.vue';
import useSettingStore from '@/store/tab-pane';
import { onMounted, ref, watch, computed, h } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  VerticalAlignMiddleOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
  ScissorOutlined,
} from '@ant-design/icons-vue';
import { systemName } from '@/config';

const router = useRouter();
const route = useRoute();
const collapsed = ref<boolean>(false);
const settingStore = useSettingStore();
const activeKey = ref(route.path);



// 组件包装器缓存
const wrapperMap = new Map();

// 包装组件，使用 path 作为组件名称
const wrap = (path: string, component: any) => {
  let wrapper = wrapperMap.get(path);
  if (!wrapper) {
    wrapper = {
      name: path,
      render() {
        return h(component);
      },
    };
    wrapperMap.set(path, wrapper);
  }
  return wrapper;
};

// 缓存的路径列表
const cachedPaths = computed(() => {
  return settingStore.menuTabs.map((tab) => tab.key);
});

// 路由监听
watch(
  () => route.path,
  (path) => {
    activeKey.value = path;
    
    // 判断是否存在该路由，不存在则添加
    if (settingStore.isTabExists(path)) return;

    // 添加路由
    const tab = {
      title: route.name as string,
      icon: route.meta.icon as string,
      key: path,
      closable: true,
      componentName: (route.meta.componentName || route.name) as string,
    };

    settingStore.addTab(tab);
  },
  {
    immediate: true,
    deep: true,
  }
);

const onTabChange = (key: string) => {
  router.push(key);
};

const onEdit = (targetKey: string, action: string) => {
  if (action === 'remove') {
    if (settingStore.menuTabs.length == 1 && route.path === '/home') return;
    settingStore.removeTab(targetKey);
    // 清除缓存的包装组件
    wrapperMap.delete(targetKey);
    if (settingStore.menuTabs.length == 0) router.push('/');
    else if (targetKey === route.path) {
      // 如果关闭的是当前标签，跳转到最后一个标签
      const lastTab = settingStore.menuTabs[settingStore.menuTabs.length - 1];
      router.push(lastTab.key);
    }
  }
};

const closeTab = ({ key }: any, index: number) => {
  const currentPath = route.path;
  switch (key) {
    case '1': // 关闭当前
      if (settingStore.menuTabs.length == 1 && currentPath === '/home') return;
      const closedKey = settingStore.menuTabs[index].key;
      settingStore.removeTab(closedKey);
      wrapperMap.delete(closedKey);
      if (settingStore.menuTabs.length == 0) {
        router.push('/');
      } else if (closedKey === currentPath) {
        const lastTab = settingStore.menuTabs[settingStore.menuTabs.length - 1];
        router.push(lastTab.key);
      }
      break;
    case '2': // 关闭右侧
      const rightTabs = settingStore.menuTabs.slice(index + 1);
      rightTabs.forEach((tab) => wrapperMap.delete(tab.key));
      settingStore.menuTabs.splice(index + 1, settingStore.menuTabs.length - index - 1);
      break;
    case '3': // 关闭左侧
      const leftTabs = settingStore.menuTabs.slice(0, index);
      leftTabs.forEach((tab) => wrapperMap.delete(tab.key));
      settingStore.menuTabs.splice(0, index);
      break;
    case '4': // 关闭全部
      if (currentPath === '/home') {
        settingStore.menuTabs.forEach((tab) => {
          if (tab.key !== '/home') wrapperMap.delete(tab.key);
        });
        settingStore.menuTabs = settingStore.menuTabs.filter((tab) => tab.key === '/home');
      } else {
        wrapperMap.clear();
        settingStore.menuTabs = [];
        router.push('/');
      }
      break;
  }
};

onMounted(() => {});
</script>

<style lang="less" scoped>
.wrap-layout {
  position: relative;
  width: 100%;
  height: 100%;
}

.tabs {
  width: 100%;
  height: 42px;
}

:deep(.ant-tabs-nav) {
  background-color: #fafafa !important;
}

.content {
  border-radius: 12px;
  height: calc(100% - 70px);
}

.content-scroll {
  background-color: #fff;
  overflow-y: scroll;
  height:100%;
}

::-webkit-scrollbar {
  display: none;
}

.layout-header {
  background: #fff !important;
  padding: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
</style>
